import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
  signal,
  Signal,
} from '@angular/core';
import type { InvoiceFilters } from '@shared/models/finance-invoice';
import { TuyauService } from '@core/tuyau';

const DEFAULT_PER_PAGE = 100;
const EMPTY_FILTERS: Signal<InvoiceFilters> = computed(() => ({}));

type PaginatorMeta = {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
};

@Injectable({ providedIn: 'root' })
export class FinanceInvoiceService {
  private tuyau = inject(TuyauService);

  private filtersSource = signal<Signal<InvoiceFilters>>(EMPTY_FILTERS);
  private filters = computed(() => this.filtersSource()());
  private perPage = signal(DEFAULT_PER_PAGE);
  private pageResetKey = computed(
    () => [this.filters(), this.perPage()] as const,
    { equal: (a, b) => a[0] === b[0] && a[1] === b[1] }
  );
  private page = linkedSignal({
    source: this.pageResetKey,
    computation: () => 1,
  });

  private invoicesResource = resource({
    params: () => {
      const source = this.filtersSource();
      if (source === EMPTY_FILTERS) return undefined;
      return {
        filters: source(),
        page: this.page(),
        perPage: this.perPage(),
      };
    },
    loader: async ({ params }) => {
      const { filters, page, perPage } = params;
      const query: Parameters<
        typeof this.tuyau.client.api.employeeV2.financeInvoice.index
      >[0]['query'] = {
        page,
        perPage,
      };

      if (filters.id) query.id = filters.id;
      if (filters.steps?.length) query.status = filters.steps as never;
      if (filters.from) query.from = filters.from;
      if (filters.to) query.to = filters.to;
      if (filters.bcVendorId) query.bcVendorId = filters.bcVendorId;
      if (filters.amountMin) query.amountMin = filters.amountMin;
      if (filters.amountMax) query.amountMax = filters.amountMax;

      return this.tuyau.client.api.employeeV2.financeInvoice.index({ query });
    },
  });

  readonly invoices = computed(() => this.invoicesResource.value()?.data ?? []);
  readonly metadata = computed(
    () =>
      (this.invoicesResource.value()?.metadata ?? null) as PaginatorMeta | null
  );
  readonly total = computed(() => this.metadata()?.total ?? 0);
  readonly currentPage = this.page.asReadonly();
  readonly currentPerPage = this.perPage.asReadonly();
  readonly count = computed(() => this.invoices().length);
  readonly isLoading = this.invoicesResource.isLoading;

  bindFilters(source: Signal<InvoiceFilters>) {
    this.filtersSource.set(source);
  }

  setPage(page: number) {
    this.page.set(page);
  }

  setPerPage(perPage: number) {
    this.perPage.set(perPage);
  }

  reload() {
    this.invoicesResource.reload();
  }
}
